/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  FETCH_SD_EXTERNALUSER_APPLICATION_STATUS,
  FETCH_SD_USER_APPLICATION_INFO,
  SET_IEG_APPLICATION_TYPE,
} from './actionTypes';

import { fetchSdExternaluserApplicationStatusUtil, fetchSdUserApplicationInfoUtil } from './utils';

/**
 * Contains the action creator functions that update the SDApplication slice of the store.
 *
 * @namespace SDApplicationActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sd_externaluser_application_status
   *
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof SDApplicationActions
   */
  static fetchSdExternaluserApplicationStatus = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchSdExternaluserApplicationStatusUtil,
      FETCH_SD_EXTERNALUSER_APPLICATION_STATUS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchSdExternaluserApplicationStatus.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDApplicationActions
   */
  static resetFetchSdExternaluserApplicationStatus = (dispatch, data) => {
    const json = {
      type: FETCH_SD_EXTERNALUSER_APPLICATION_STATUS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchSdExternaluserApplicationStatus.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDApplicationActions
   */
  static resetFetchSdExternaluserApplicationStatusError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_SD_EXTERNALUSER_APPLICATION_STATUS.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sd_user_application_info
   *
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof SDApplicationActions
   */
  static fetchSdUserApplicationInfo = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchSdUserApplicationInfoUtil,
      FETCH_SD_USER_APPLICATION_INFO,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchSdUserApplicationInfo.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDApplicationActions
   */
  static resetFetchSdUserApplicationInfo = (dispatch, data) => {
    const json = {
      type: FETCH_SD_USER_APPLICATION_INFO.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchSdUserApplicationInfo.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDApplicationActions
   */
  static resetFetchSdUserApplicationInfoError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_SD_USER_APPLICATION_INFO.failureAction);
  };

  /**
   * Dispatches the data to the store.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDApplicationActions
   */
  static setIegApplicationType = (dispatch, data) => {
    const json = {
      type: SET_IEG_APPLICATION_TYPE,
      payload: data || {},
    };
    dispatch(json);
  };
}
